import React, { useState, useEffect, useMemo, useCallback } from 'react';
import dataShare from '../../Common/dataShare';
import { notify } from 'react-notify-toast';
import './DataDogReportPage.css';
import { Bar } from 'react-chartjs-2';
import {
  Box, Card, Typography, Dialog, DialogTitle, DialogContent,
  Button, MenuItem, Select, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TableSortLabel, Paper, TextField,
  IconButton, CircularProgress
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import 'chartjs-plugin-datalabels';

export default function DataDogReportPage() {
  const [chartData, setChartData] = useState([]);
  const [gridData, setGridData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [clientId, setClientId] = useState('');
  const [requestStatus, setRequestStatus] = useState('Success');
  const [environment, setEnvironment] = useState('prod');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('FirstName');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  const showNotification = (type, message) => {
    const styles = {
      error: { background: '#d50000', text: '#FAFAFA' },
      warning: { background: '#F57F17', text: '#FAFAFA' },
      success: { background: '#4CAF50', text: '#FAFAFA' }
    };
    notify.show(message, 'custom', 5000, styles[type] || styles.warning);
  };

  const getAllDataDogReports = useCallback(async (status, env) => {
    setLoading(true);
    try {
      const response = await new Promise((resolve, reject) => {
        dataShare.getAllReportForPast6Months(status, env, (error, res) => {
          if (error) reject(error);
          else resolve(res);
        });
      });
      setClientId(response.ClientId);
      setRequestStatus(response.RequestStatus);
      setEnvironment(response.Environment);
      setChartData(response.TransactionLogs);
      setGridData(response.ApplicationLogs.map((row, index) => ({ id: index + 1, ...row })));
    } catch (error) {
      showNotification('error', 'Internal server error while fetching site information, please try after sometime.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getAllDataDogReports('Success', 'prod');
  }, [getAllDataDogReports]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
  };

  const fetchMonthData = async (label) => {
    try {
      const response = await new Promise((resolve, reject) => {
        dataShare.getDataDogReportsPerMonth(label, requestStatus, environment, (error, res) => {
          if (error) reject(error);
          else resolve(res);
        });
      });
      const rowsWithId = response.map((row, index) => ({ id: index + 1, ...row }));
      setSelectedMonth(rowsWithId);
      setOpenDialog(true);
    } catch (error) {
      showNotification('error', 'Error fetching month details');
    }
  };

  const data = useMemo(() => ({
    labels: chartData.map(item => item.Month),
    datasets: [{
      label: 'No of Apps',
      data: chartData.map(item => Number(item.Count) || 0),
      backgroundColor: '#29b6f6',
      borderWidth: 0
    }]
  }), [chartData]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 30 } },
    scales: {
      yAxes: [{ ticks: { beginAtZero: true, min: 0, fontColor: '#000' }, gridLines: { display: true }, scaleLabel: { display: true, labelString: 'No of Apps' } }],
      xAxes: [{ ticks: { fontColor: '#000' }, gridLines: { display: false }, scaleLabel: { display: true, labelString: 'Month' } }]
    },
    plugins: {
      datalabels: { display: true, color: '#29b6f6', font: { weight: 'bold', size: 14 }, anchor: 'end', align: 'top' }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const index = elements[0]._index;
        const label = data.labels[index];
        fetchMonthData(label);
      }
    }
  };

  const sortedRows = useMemo(() => stableSort(gridData, getComparator(order, orderBy)), [gridData, order, orderBy]);
  const sortedTransactionRows = useMemo(() => stableSort(selectedMonth, getComparator(order, orderBy)), [selectedMonth, order, orderBy]);

  const appColumns = [
    { field: 'FirstName', headerName: 'First Name' },
    { field: 'LastName', headerName: 'Last Name' },
    { field: 'EmailId', headerName: 'Email ID' },
    { field: 'VersionTitle', headerName: 'Version Title' },
    { field: 'DirectAppId', headerName: 'Direct App ID' },
    { field: 'ApplicationNumber', headerName: 'LOS App Number' },
    { field: 'CreateDate', headerName: 'Create Date' },
    { field: 'UpdateDate', headerName: 'Update Date' }
  ];

  const transactionColumns = [
    { field: 'LoggedDate', headerName: 'Logged Date' },
    { field: 'AppSubmitURL', headerName: 'App Submit URL' },
    { field: 'ClientPageUrl', headerName: 'Client Page URL' },
    { field: 'DirectAppId', headerName: 'Direct AppId' },
    { field: 'ApplicationNumber', headerName: 'LOS Application Number' },
    { field: 'RequestStatus', headerName: 'Request Status' },
    { field: 'Published', headerName: 'Published' },
    { field: 'FromPreview', headerName: 'From Preview' },
    { field: 'VersionId', headerName: 'Version ID' }
  ];

  return (
    <Box style={{ padding: '16px', marginTop: '64px' }}>
      {/* Filters */}
      <Box display="flex" gap="24px" alignItems="flex-start">
        <Box style={{ marginRight: '12px' }}>
          <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>Request Status *</Typography>
          <Select className="select-style" value={requestStatus} onChange={(e) => { setRequestStatus(e.target.value); getAllDataDogReports(e.target.value, environment); }}>
            <MenuItem value="Success">Success</MenuItem>
            <MenuItem value="Error">Error</MenuItem>
            <MenuItem value="Failure">Failure</MenuItem>
          </Select>
        </Box>
        <Box style={{ marginRight: '12px' }}>
          <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>Submit to LOS *</Typography>
          <Select className="select-style" value={environment} onChange={(e) => { setEnvironment(e.target.value); getAllDataDogReports(requestStatus, e.target.value); }}>
            <MenuItem value="qa">LOS QA</MenuItem>
            <MenuItem value="prod">LOS PROD</MenuItem>
          </Select>
        </Box>
        <Box>
          <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>Client GUID</Typography>
          <TextField value={clientId} InputProps={{ readOnly: true }} style={{ backgroundColor: '#f5f5f5', borderRadius: '8px', padding: '8px 16px', minWidth: '150px' }} />
        </Box>
      </Box>

      {/* Chart */}
      <Box style={{ padding: '12px' }}>
        <Card style={{ padding: '30px', marginBottom: '16px' }}>
          <Typography variant="h6">Rolling 6 months Volume</Typography>
          <div style={{ width: '100%', height: '400px' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" style={{ height: '400px' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Bar data={data} options={options} plugins={[require('chartjs-plugin-datalabels')]} />
            )}
          </div>
        </Card>
      </Box>

      {/* Applications Table */}
      <Card>
        <Typography variant="h6" style={{ padding: '12px' }}>Applications</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {appColumns.map((col) => (
                  <TableCell key={col.field} style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                    <TableSortLabel active={orderBy === col.field} direction={orderBy === col.field ? order : 'asc'} onClick={() => handleRequestSort(col.field)}>
                      {col.headerName}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                <TableRow key={row.id}>
                  {appColumns.map((col) => (
                    <TableCell key={col.field}>{row[col.field]}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div" count={gridData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Card>

      {/* Dialog for Transactions */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="lg">
        <DialogTitle>DEFI DIRECT TRANSACTIONS</DialogTitle>
        <IconButton aria-label="close" onClick={() => setOpenDialog(false)} style={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {transactionColumns.map((col) => (
                    <TableCell key={col.field} style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                      <TableSortLabel active={orderBy === col.field} direction={orderBy === col.field ? order : 'asc'} onClick={() => handleRequestSort(col.field)}>
                        {col.headerName}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTransactionRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                  <TableRow key={row.id}>
                    {transactionColumns.map((col) => (
                      <TableCell key={col.field}>{row[col.field]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination rowsPerPageOptions={[10, 25, 50]} component="div" count={selectedMonth.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
          <Box style={{ marginTop: '16px', textAlign: 'right' }}>
            <Button variant="contained" color="primary" onClick={() => setOpenDialog(false)}>Close</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}



// WEBPACK FOOTER //
// ./src/admin/components/Main/ReportsPage/DataDogReportPage.js